import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ReminderPicker from "./ReminderPicker";
import {
    reminderFieldsFromPreset,
    reminderPresetFromTask,
} from "../utils/taskReminderSync";

const EditableField = ({
    value,
    field,
    onChange,
    task,
    leadOptions = [],
    assignOptions = [],
    assignedBy,
    apiUrl,
    readOnly = false,
}) => {
    const [editing, setEditing] = useState(false);
    const [temp, setTemp] = useState(value);
    const [reminderPreset, setReminderPreset] = useState(
        task ? reminderPresetFromTask(task) : "none"
    );
    const [customMinutes, setCustomMinutes] = useState(task?.reminder_minutes || 15);
    const isDisabled = readOnly || assignedBy === "Admin";

    useEffect(() => {
        setTemp(value);
    }, [value]);

    useEffect(() => {
        if (task && field === "reminder_preset") {
            setReminderPreset(reminderPresetFromTask(task));
            setCustomMinutes(task.reminder_minutes || 15);
        }
    }, [task, field]);

    const handleBlur = () => {
        setEditing(false);
        if (field === "reminder_preset") {
            const fields = reminderFieldsFromPreset(reminderPreset, customMinutes);
            onChange(fields);
            return;
        }
        if (temp !== value) onChange(temp);
        else setTemp(value);
    };

    const handleChange = (e) => {
        setTemp(e.target.value);
        onChange(e.target.value);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setTemp(file.name);
            onChange(file);
        }
    };

    const baseClass =
        "w-full bg-surface-2 border-b border-primary outline-none px-1 py-0.5 rounded-sm text-[12px] text-ink";

    const renderInput = () => {
        switch (field) {
            case "due_date":
                return (
                    <input
                        type="date"
                        min={new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" })}
                        className={baseClass}
                        value={
                            temp
                                ? new Date(temp).toLocaleDateString("en-CA", {
                                      timeZone: "Asia/Kolkata",
                                  })
                                : ""
                        }
                        autoFocus
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                );
            case "start_time":
                return (
                    <input
                        type="time"
                        className={baseClass}
                        value={temp ? String(temp).slice(0, 5) : ""}
                        autoFocus
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                );
            case "reminder_preset":
                return (
                    <div onBlur={handleBlur}>
                        <ReminderPicker
                            value={reminderPreset}
                            customMinutes={customMinutes}
                            onChange={({ preset, customMinutes: mins }) => {
                                setReminderPreset(preset);
                                if (mins) setCustomMinutes(mins);
                            }}
                        />
                    </div>
                );
            case "priority":
            case "repetition":
            case "assign_to":
            case "list_id":
                return (
                    <select
                        className={baseClass}
                        value={temp ?? ""}
                        autoFocus
                        onChange={(e) => setTemp(e.target.value)}
                        onBlur={handleBlur}
                    >
                        {field === "priority" && (
                            <>
                                <option value="high">High</option>
                                <option value="medium">Medium</option>
                                <option value="low">Low</option>
                            </>
                        )}
                        {field === "repetition" && (
                            <>
                                <option value="none">Does not repeat</option>
                                <option value="daily">Daily</option>
                                <option value="weekdays">Weekdays</option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                                <option value="annually">Yearly</option>
                                <option value="custom">Custom…</option>
                            </>
                        )}
                        {field === "assign_to" &&
                            assignOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        {field === "list_id" &&
                            leadOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                    </select>
                );
            case "notes":
                return (
                    <textarea
                        className={baseClass}
                        value={temp ?? ""}
                        rows={2}
                        autoFocus
                        onChange={(e) => setTemp(e.target.value)}
                        onBlur={handleBlur}
                    />
                );
            case "attachment":
                if (editing) {
                    return (
                        <input
                            type="file"
                            className={baseClass}
                            autoFocus
                            onChange={handleFileChange}
                        />
                    );
                }
                return value ? (
                    typeof value === "string" ? (
                        <a
                            href={`${apiUrl}/${value}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-info underline text-xs"
                        >
                            View Attachment
                        </a>
                    ) : (
                        <span className="text-ink-secondary text-xs">{value.name}</span>
                    )
                ) : (
                    <span className="text-ink-muted italic text-xs">No file</span>
                );
            default:
                return (
                    <input
                        type="text"
                        className={baseClass}
                        value={temp ?? ""}
                        autoFocus
                        onChange={(e) => setTemp(e.target.value)}
                        onBlur={handleBlur}
                    />
                );
        }
    };

    if (isDisabled) {
        return (
            <p className="text-ink-secondary truncate text-[12px]">{value || "—"}</p>
        );
    }

    return (
        <div
            className="w-full text-[12px]"
            onClick={() => setEditing(true)}
            onMouseEnter={() => !editing && setEditing(true)}
        >
            <AnimatePresence mode="wait">
                {editing ? (
                    <motion.div
                        key="input"
                        initial={{ opacity: 0, y: -3 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 3 }}
                        transition={{ duration: 0.15 }}
                    >
                        {renderInput()}
                    </motion.div>
                ) : (
                    <motion.p
                        key="text"
                        initial={{ opacity: 0, y: 3 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -3 }}
                        transition={{ duration: 0.15 }}
                        className="text-ink-secondary cursor-pointer truncate text-[12px]"
                    >
                        {value || "—"}
                    </motion.p>
                )}
            </AnimatePresence>
        </div>
    );
};

export default EditableField;
